import { Client } from "@/Common/Api/Client";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { InformationLoadingComponent } from "@/Common/Components/Loader/InformationLoadingComponent";
import { Popup } from "@/Common/Components/Modals/Popup";
import { IMap } from "@/Common/Models/IMap";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { ValueSetType } from "../Constants/ValueSetType";
import { PractitionerModel } from "../Models/PractitionerModel";
import { RvqDomainValue } from "../Models/RvqDomainValue";
import { RvqPractitionerPreference } from "../Models/RvqPractitionerPreference";
import { GetRvqPractitionersBySearchResponse } from "../ResponseModels/GetRvqPractitionersBySearchResponse";
import { GetRvqPreferencesResponse } from "../ResponseModels/GetRvqPreferencesResponse";
import { GetValuesResponse } from "../ResponseModels/GetValuesResponse";
import { RvqPractitionerSearch } from "./RvqPractitionerSearch";

interface RvqPractitionerSearchPopupProps {
    existingPractitionerPreferences: RvqPractitionerPreference[];
    onClose: () => void;
    onPractitionerSave: (practitioner: PractitionerModel, preferencesToUpdate: RvqPractitionerPreference[]) => void;
    isRvqAuthenticated: boolean;
}

class RvqPractitionerSearchPopupState {
    public searchFields: {
        givenName: string;
        familyName: string;
        role: string;
        permitNumber: string;
    };
    public currentSelectedPractitioner: PractitionerModel;
    public practitioners: PractitionerModel[];
    public isSearchingForPractitioner: boolean;
    public roles: IMap<RvqDomainValue>;

    public constructor(init?: Partial<RvqPractitionerSearchPopupState>) {
        Object.assign(this, init);
    }

    public static default(): RvqPractitionerSearchPopupState {
        return new RvqPractitionerSearchPopupState({
            searchFields: {
                givenName: "",
                familyName: "",
                role: null,
                permitNumber: ""
            },
            currentSelectedPractitioner: null,
            practitioners: [],
            isSearchingForPractitioner: false,
            roles: {}
        });
    }
}
class RvqPractitionerSearchPopup extends BaseReactComponent<RvqPractitionerSearchPopupProps, RvqPractitionerSearchPopupState> {
    public constructor(props: RvqPractitionerSearchPopupProps) {
        super(props);
        this.state = RvqPractitionerSearchPopupState.default();
    }

    public async componentDidMount(): Promise<void> {
        const response = await Client.postAsync<GetValuesResponse>(
            ServerOperations.Server.QuebecVaccinationRegistry_GetValues,
            {
                Type: [ValueSetType.PractitionnerRole]
            }
        );
        if (response.isSuccess)
            this.mutateState(s => s.roles = response.data?.Types?.[ValueSetType.PractitionnerRole] ?? {});
    }

    public render(): JSX.Element {
        return (
            <Popup
                centeredHorizontally
                draggable={false}
                width={450}
                topPosition={100}
                manageOverlay
                style={{ display: "flex", flexFlow: "column", overflow: "visible" }}
                Id="practitioner-search"
                altView
            >
                <div className="p-header">
                    <a className="close" aria-label="Close" onClick={() => this.props.onClose()}> ×</a>
                    <h4>{cacheStore.GetString("QuebecVaccinationRegistry_AddANewVaccinatorLabel").toUpperCase()}</h4>
                </div>

                <div className="p-body" style={{ alignSelf: "stretch", flexGrow: 1, overflowY: "visible" }}>
                    <RvqPractitionerSearch
                        roles={this.state.roles}
                        searchFields={this.state.searchFields}
                        onChange={(value, key) => this.mutateState(s => {
                            s.currentSelectedPractitioner = null;
                            s.searchFields[key] = value;
                            s.practitioners = [];
                            s.isSearchingForPractitioner = false;
                        })}
                        practitioners={this.state.practitioners}
                        onPractitionerSelect={() => { }}
                        onEnterKeyDown={() => this.searchForPractitioner()}
                        showList={false}
                    />
                    {this.state.practitioners.any() && (
                        <div className="row">
                            <div className="col-md-12">
                                <div
                                    id="practitioners-list-container"
                                    style={{ maxHeight: "200px", width: "100%", overflowY: "auto" }}
                                >
                                    <div className="results-container">
                                        <ul className="results-list">
                                            {this.state.practitioners.map(practitioner => (
                                                <li key={practitioner.Id}>
                                                    <div className="flex-container">
                                                        <a
                                                            onClick={() => {
                                                                this.mutateState(s => {
                                                                    s.searchFields = {
                                                                        givenName: practitioner.GivenName,
                                                                        familyName: practitioner.FamilyName,
                                                                        role: practitioner.Roles.firstOrDefault()?.Code ?? null,
                                                                        permitNumber: practitioner.PermitNumber.toString()
                                                                    };
                                                                    s.currentSelectedPractitioner = practitioner;
                                                                    s.practitioners = [];
                                                                });
                                                            }}
                                                        >
                                                            {PractitionerModel.getDisplayName(practitioner)}
                                                        </a>
                                                    </div>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
                <div className="p-footer">
                    <div className="row">
                        <div className="col-md-12" style={{ display: "flex", justifyContent: "space-between" }}>
                            <div>
                                {this.state.isSearchingForPractitioner && <InformationLoadingComponent showLoadingImageOnly />}
                            </div>
                            <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
                                <button
                                    className="btn btn-default push-right-5"
                                    onClick={() => this.props.onClose()}
                                >
                                    {cacheStore.GetString("CancelLabel")}
                                </button>
                                <button
                                    className="btn btn-primary push-right-5"
                                    disabled={!this.canSearchForPractitioner() || !!this.state.currentSelectedPractitioner}
                                    onClick={() => this.searchForPractitioner()}
                                >
                                    {cacheStore.GetString("SearchLabel")}
                                </button>
                                <button
                                    disabled={!this.isValidPractitioner()}
                                    className="btn btn-primary"
                                    onClick={async () => {
                                        const practitioner = this.state.currentSelectedPractitioner;
                                        const response = await Client.postAsync<GetRvqPreferencesResponse>(
                                            ServerOperations.Server.QuebecVaccinationRegistry_AddPreference,
                                            {
                                                EmployeeId: sessionStore.getCurrentEmployee().ContactFacetId,
                                                PractitionerPreference: PractitionerModel.toPreference(practitioner)
                                            }
                                        );
                                        this.props.onPractitionerSave(practitioner, response.data?.PractitionerPreferences.map(x => new RvqPractitionerPreference(x)) ?? []);
                                    }}
                                >
                                    {cacheStore.GetString("SaveLabel")}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </Popup>
        );
    }

    private searchForPractitioner(): void {
        if (!this.canSearchForPractitioner())
            return;

        const {
            givenName,
            familyName,
            permitNumber,
            role
        } = this.state.searchFields;

        this.mutateState(
            s => s.isSearchingForPractitioner = true,
            async () => {
                const response = await Client.postAsync<GetRvqPractitionersBySearchResponse>(
                    this.props.isRvqAuthenticated
                        ? ServerOperations.Server.QuebecVaccinationRegistry_GetPractitionersBySearch
                        : ServerOperations.Server.QuebecVaccinationRegistry_Offline_GetPractitionersBySearch,
                    {
                        GivenName: givenName,
                        FamilyName: familyName,
                        PermitNumber: permitNumber,
                        RoleCode: role
                    }
                );

                if (response.errors.any())
                    sessionStore.showPopup(response.errors);

                const practitioners = (response.data?.Practitioners?.map(p => new PractitionerModel(p)) ?? [])
                    .filter(pref => !this.props.existingPractitionerPreferences.any(existingPref => pref.Id.toString() === existingPref.comparableRvqId));

                this.mutateState(s => {
                    s.practitioners = practitioners;
                    s.isSearchingForPractitioner = false;
                });
            }
        );
    }

    private isValidPractitioner(): boolean {
        return this.state.currentSelectedPractitioner !== null;
    }

    private canSearchForPractitioner(): boolean {
        const {
            givenName,
            familyName,
            permitNumber
        } = this.state.searchFields;
        return (
            !IsNullOrWhiteSpace(givenName)
            || !IsNullOrWhiteSpace(familyName)
            || !IsNullOrWhiteSpace(permitNumber)
        ) && !this.state.isSearchingForPractitioner;
    }
}

export { RvqPractitionerSearchPopup };
